import axios from 'axios';

// Debug: Check environment variables
console.log('🔧 Environment check:');
console.log('- REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
console.log('- NODE_ENV:', process.env.NODE_ENV);

// Create axios instance with default config
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api';
console.log('🚀 Using API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Increased timeout for Render
});

// ==================== REQUEST INTERCEPTOR ====================
api.interceptors.request.use(
  (config) => {
    console.log('📤 API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: config.data ? (typeof config.data === 'object' ? { ...config.data, password: config.data.password ? '***HIDDEN***' : undefined } : config.data) : undefined
    });
    
    const token = localStorage.getItem('token');
    if (token) {
      console.log('🔑 Adding token to request');
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.log('⚠️ No token found in localStorage');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ==================== RESPONSE INTERCEPTOR ====================
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    
    // Return the data directly
    return response.data;
  },
  (error) => {
    console.error('❌ ========== API ERROR DETAILS ==========');
    console.error('URL:', error.config?.url);
    console.error('Method:', error.config?.method);
    console.error('Status:', error.response?.status);
    console.error('Status Text:', error.response?.statusText);
    console.error('Error Data:', error.response?.data);
    console.error('Error Message:', error.message);
    console.error('❌ ========================================');
    
    if (error.response?.status === 401) {
      console.warn('🔒 Unauthorized - Invalid token or credentials');
      
      // Clear storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not on login page
      if (!window.location.pathname.includes('/login')) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }

    // Create a better error object
    const errorResponse = {
      success: false,
      message: error.response?.data?.message || error.message || 'Network error',
      status: error.response?.status,
      data: error.response?.data,
      originalError: error
    };

    return Promise.reject(errorResponse);
  }
);

// ==================== API TEST FUNCTIONS ====================
export const testConnection = async () => {
  try {
    console.log('🔍 Testing API connection...');
    const response = await api.get('/health');
    console.log('✅ API Connection test successful:', response);
    return response;
  } catch (error) {
    console.error('❌ API Connection test failed:', error.message);
    return { 
      success: false, 
      message: `Cannot connect to API: ${error.message}`,
      error 
    };
  }
};

// ==================== WEBSOCKET URL HELPER ====================
export const getWebSocketURL = () => {
  // Get base URL from environment or use default
  const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api';
  
  console.log('🔌 Calculating WebSocket URL from:', baseURL);
  
  // Convert https://fleet-county-backend.onrender.com/api to wss://fleet-county-backend.onrender.com
  let wsURL;
  
  if (baseURL.includes('https://')) {
    wsURL = baseURL.replace('https://', 'wss://').replace('/api', '');
  } else if (baseURL.includes('http://')) {
    wsURL = baseURL.replace('http://', 'ws://').replace('/api', '');
  } else {
    // Fallback
    wsURL = 'wss://fleet-county-backend.onrender.com';
  }
  
  console.log('🔌 WebSocket URL:', wsURL);
  return wsURL;
};

// WebSocket connection helper
export const createWebSocketConnection = (token, onMessage, onOpen, onClose, onError) => {
  if (!token) {
    console.error('❌ No token provided for WebSocket connection');
    return null;
  }
  
  const wsURL = getWebSocketURL();
  const wsUrl = `${wsURL}/?token=${token}`;
  
  console.log('🔌 Creating WebSocket connection to:', wsUrl);
  
  const ws = new WebSocket(wsUrl);
  
  ws.onopen = (event) => {
    console.log('✅ WebSocket connected');
    if (onOpen) onOpen(event);
    
    // Send authentication message
    ws.send(JSON.stringify({
      type: 'authenticate',
      token: token
    }));
  };
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      console.log('📩 WebSocket message:', data);
      if (onMessage) onMessage(data);
    } catch (error) {
      console.error('❌ WebSocket message parse error:', error);
    }
  };
  
  ws.onclose = (event) => {
    console.log('🔌 WebSocket disconnected:', event.code, event.reason);
    if (onClose) onClose(event);
    
    // Attempt to reconnect after 5 seconds
    setTimeout(() => {
      console.log('🔄 Attempting to reconnect WebSocket...');
      const newToken = localStorage.getItem('token');
      if (newToken) {
        createWebSocketConnection(newToken, onMessage, onOpen, onClose, onError);
      }
    }, 5000);
  };
  
  ws.onerror = (error) => {
    console.error('❌ WebSocket error:', error);
    if (onError) onError(error);
  };
  
  return ws;
};

// ==================== API ENDPOINTS ====================
export const authAPI = {
  login: (credentials) => {
    console.log('🔑 Login attempt with email:', credentials.email);
    return api.post('/auth/login', credentials);
  },
  registerCompany: (data) => api.post('/auth/register-company', data),
  registerDriver: (data) => api.post('/auth/register-driver', data),
  
  // ✅ FIXED: Company admin endpoint for adding drivers
  companyAddDriver: (data) => {
    console.log('🏢 Company admin adding driver:', { 
      name: data.name, 
      email: data.email,
      licenseNumber: data.licenseNumber
    });
    
    const driverData = {
      name: data.name,
      email: data.email,
      password: data.password,
      phone: data.phone || '',
      licenseNumber: data.licenseNumber,
      driverType: data.driverType || 'truck',
      experience: data.experience || 0,
      location: data.location || '',
      status: data.status || 'active',
      availability: data.availability || 'available'
    };
    
    console.log('📤 Sending driver data:', { ...driverData, password: '***HIDDEN***' });
    return api.post('/auth/company/add-driver', driverData);
  },
  
  getMe: () => {
    console.log('👤 Getting user profile...');
    return api.get('/auth/me');
  },
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  test: () => api.get('/auth/test'),
  
  // ✅ ADDED: New simplified endpoint as fallback
  companyAddDriverSimple: (data) => {
    console.log('🏢 Using simplified company-add-driver endpoint');
    return api.post('/auth/company-add-driver', data);
  }
};

export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  getProfile: () => api.get('/companies/profile'),
  updateProfile: (data) => api.put('/companies/profile', data),
  getMyProfile: () => api.get('/companies/me/profile'),
  getStats: () => api.get('/companies/stats'),
  getDrivers: (id) => api.get(`/companies/${id}/drivers`),
  getVehicles: (id) => api.get(`/companies/${id}/vehicles`),
  addDriver: (id, data) => api.post(`/companies/${id}/drivers`, data),
  removeDriver: (companyId, driverId) => api.delete(`/companies/${companyId}/drivers/${driverId}`),
  addVehicle: (id, data) => api.post(`/companies/${id}/vehicles`, data),
  removeVehicle: (companyId, vehicleId) => api.delete(`/companies/${companyId}/vehicles/${vehicleId}`),
  updateSettings: (id, data) => api.put(`/companies/${id}/settings`, data),
  getAnalytics: (id) => api.get(`/companies/${id}/analytics`),
  getReports: (id) => api.get(`/companies/${id}/reports`),
  getSubscription: (id) => api.get(`/companies/${id}/subscription`),
  updateSubscription: (id, data) => api.put(`/companies/${id}/subscription`, data),
};

export const vehiclesAPI = {
  getAll: () => api.get('/vehicles'),
  getById: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  assignDriver: (vehicleId, driverId) => api.post(`/vehicles/${vehicleId}/assign-driver`, { driverId }),
  unassignDriver: (vehicleId) => api.post(`/vehicles/${vehicleId}/unassign-driver`),
  updateLocation: (vehicleId, data) => api.put(`/vehicles/${vehicleId}/location`, data),
  updateFuel: (vehicleId, data) => api.put(`/vehicles/${vehicleId}/fuel`, data),
  getAvailable: () => api.get('/vehicles/available'),
  getStats: () => api.get('/vehicles/stats'),
  enableTracking: (id) => api.post(`/vehicles/${id}/enable-tracking`),
  disableTracking: (id) => api.post(`/vehicles/${id}/disable-tracking`),
  updateService: (id, data) => api.put(`/vehicles/${id}/service`, data),
  emergencyCleanup: () => api.post('/vehicles/emergency-cleanup'),
  updateFatigue: (id, data) => api.put(`/vehicles/${id}/fatigue`, data),
  getMaintenanceHistory: (id) => api.get(`/vehicles/${id}/maintenance`),
  addMaintenance: (id, data) => api.post(`/vehicles/${id}/maintenance`, data),
  getTrips: (id) => api.get(`/vehicles/${id}/trips`),
  getAlerts: (id) => api.get(`/vehicles/${id}/alerts`),
  getDocuments: (id) => api.get(`/vehicles/${id}/documents`),
  uploadDocument: (id, data) => api.post(`/vehicles/${id}/documents`, data),
};

export const driversAPI = {
  getAll: () => api.get('/drivers'),
  getById: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  updateStatus: (id, data) => api.put(`/drivers/${id}/status`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
  getAssignedVehicles: (id) => api.get(`/drivers/${id}/vehicles`),
  assignVehicle: (driverId, data) => api.post(`/drivers/${driverId}/assign-vehicle`, data),
  unassignVehicle: (driverId) => api.post(`/drivers/${driverId}/unassign-vehicle`),
  getRatings: (driverId) => api.get(`/drivers/${driverId}/ratings`),
  rateDriver: (driverId, data) => api.post(`/drivers/${driverId}/rate`, data),
  
  // ✅ ADDED: Remove driver from company (send back to Talent Hub)
  removeFromCompany: (driverId, data) => {
    console.log('🚀 [Remove from Company] Removing driver:', driverId);
    return api.post(`/drivers/${driverId}/remove-from-company`, data);
  },
  
  getAvailable: () => api.get('/drivers/available'),
  startTracking: (driverId, data) => api.post(`/drivers/${driverId}/tracking/start`, data),
  stopTracking: (driverId) => api.post(`/drivers/${driverId}/tracking/stop`, {}),
  getDriverProfile: () => api.get('/drivers/me'),
  updateDriverProfile: (data) => api.put('/drivers/me', data),
  changeDriverPassword: (data) => api.put('/drivers/me/password', data),
  updateDriverFatigue: (data) => api.put('/drivers/me/fatigue', data),
  updateDriverLocation: (data) => api.put('/drivers/me/location', data),
  getDriverDocuments: () => api.get('/drivers/me/documents'),
  uploadDriverDocument: (data) => api.post('/drivers/me/documents', data),
  getTrips: (id) => api.get(`/drivers/${id}/trips`),
  getAlerts: (id) => api.get(`/drivers/${id}/alerts`),
  getPerformance: (id) => api.get(`/drivers/${id}/performance`),
  getSchedule: (id) => api.get(`/drivers/${id}/schedule`),
  updateSchedule: (id, data) => api.put(`/drivers/${id}/schedule`, data),
};

export const alertsAPI = {
  getAll: () => api.get('/alerts'),
  getById: (id) => api.get(`/alerts/${id}`),
  create: (data) => api.post('/alerts', data),
  update: (id, data) => api.put(`/alerts/${id}`, data),
  delete: (id) => api.delete(`/alerts/${id}`),
  getStats: () => api.get('/alerts/stats'),
  markAsResolved: (id) => api.put(`/alerts/${id}/resolve`),
  markAsAcknowledged: (id) => api.put(`/alerts/${id}/acknowledge`),
  getUnresolved: () => api.get('/alerts/unresolved'),
  getByVehicle: (vehicleId) => api.get(`/alerts/vehicle/${vehicleId}`),
  getByDriver: (driverId) => api.get(`/alerts/driver/${driverId}`),
  getByCompany: (companyId) => api.get(`/alerts/company/${companyId}`),
};

export const analyticsAPI = {
  getDashboardStats: () => api.get('/analytics/dashboard'),
  getVehicleAnalytics: () => api.get('/analytics/vehicles'),
  getDriverAnalytics: () => api.get('/analytics/drivers'),
  getAlertAnalytics: () => api.get('/analytics/alerts'),
  getCompanyAnalytics: () => api.get('/analytics/companies'),
  getPerformanceMetrics: (period = 'daily') => api.get(`/analytics/performance?period=${period}`),
  getRevenueAnalytics: (period = 'monthly') => api.get(`/analytics/revenue?period=${period}`),
  getUtilizationAnalytics: () => api.get('/analytics/utilization'),
  getGeographicAnalytics: () => api.get('/analytics/geographic'),
  getSafetyAnalytics: () => api.get('/analytics/safety'),
};

export const tripsAPI = {
  getAll: () => api.get('/trips'),
  getById: (id) => api.get(`/trips/${id}`),
  create: (data) => api.post('/trips', data),
  update: (id, data) => api.put(`/trips/${id}`, data),
  delete: (id) => api.delete(`/trips/${id}`),
  getByVehicle: (vehicleId) => api.get(`/trips/vehicle/${vehicleId}`),
  getByDriver: (driverId) => api.get(`/trips/driver/${driverId}`),
  startTrip: (data) => api.post('/trips/start', data),
  endTrip: (tripId, data) => api.put(`/trips/${tripId}/end`, data),
  getActiveTrips: () => api.get('/trips/active'),
  getCompletedTrips: () => api.get('/trips/completed'),
  getTripAnalytics: (period = 'daily') => api.get(`/trips/analytics?period=${period}`),
  getTripRoute: (tripId) => api.get(`/trips/${tripId}/route`),
  updateTripRoute: (tripId, data) => api.put(`/trips/${tripId}/route`, data),
  addTripNote: (tripId, data) => api.post(`/trips/${tripId}/notes`, data),
  getTripNotes: (tripId) => api.get(`/trips/${tripId}/notes`),
};

export const maintenanceAPI = {
  getAll: () => api.get('/maintenance'),
  getById: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  delete: (id) => api.delete(`/maintenance/${id}`),
  getByVehicle: (vehicleId) => api.get(`/maintenance/vehicle/${vehicleId}`),
  markAsCompleted: (id) => api.put(`/maintenance/${id}/complete`),
  schedule: (data) => api.post('/maintenance/schedule', data),
  getUpcoming: () => api.get('/maintenance/upcoming'),
  getOverdue: () => api.get('/maintenance/overdue'),
  getHistory: (vehicleId) => api.get(`/maintenance/vehicle/${vehicleId}/history`),
  getCostAnalytics: () => api.get('/maintenance/cost-analytics'),
  addMaintenanceNote: (id, data) => api.post(`/maintenance/${id}/notes`, data),
  getMaintenanceNotes: (id) => api.get(`/maintenance/${id}/notes`),
  uploadMaintenanceDocument: (id, data) => api.post(`/maintenance/${id}/documents`, data),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnread: () => api.get('/notifications/unread'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  getCount: () => api.get('/notifications/count'),
  createNotification: (data) => api.post('/notifications', data),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear'),
};

export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  getCompanySettings: () => api.get('/settings/company'),
  updateCompanySettings: (data) => api.put('/settings/company', data),
  getAlertSettings: () => api.get('/settings/alerts'),
  updateAlertSettings: (data) => api.put('/settings/alerts', data),
  getNotificationSettings: () => api.get('/settings/notifications'),
  updateNotificationSettings: (data) => api.put('/settings/notifications', data),
  getTrackingSettings: () => api.get('/settings/tracking'),
  updateTrackingSettings: (data) => api.put('/settings/tracking', data),
  getSecuritySettings: () => api.get('/settings/security'),
  updateSecuritySettings: (data) => api.put('/settings/security', data),
};

export const reportsAPI = {
  generateReport: (type, data) => api.post(`/reports/${type}`, data),
  getReportHistory: () => api.get('/reports/history'),
  downloadReport: (id) => api.get(`/reports/${id}/download`, { responseType: 'blob' }),
  getReportTemplates: () => api.get('/reports/templates'),
  createReportTemplate: (data) => api.post('/reports/templates', data),
  updateReportTemplate: (id, data) => api.put(`/reports/templates/${id}`, data),
  deleteReportTemplate: (id) => api.delete(`/reports/templates/${id}`),
  scheduleReport: (data) => api.post('/reports/schedule', data),
  getScheduledReports: () => api.get('/reports/scheduled'),
  updateScheduledReport: (id, data) => api.put(`/reports/scheduled/${id}`, data),
  deleteScheduledReport: (id) => api.delete(`/reports/scheduled/${id}`),
};

export const locationsAPI = {
  getVehicleLocations: () => api.get('/locations/vehicles'),
  getVehicleLocation: (vehicleId) => api.get(`/locations/vehicles/${vehicleId}`),
  updateLocation: (vehicleId, data) => api.put(`/locations/vehicles/${vehicleId}`, data),
  getGeofences: () => api.get('/locations/geofences'),
  createGeofence: (data) => api.post('/locations/geofences', data),
  deleteGeofence: (id) => api.delete(`/locations/geofences/${id}`),
  getGeofenceAlerts: () => api.get('/locations/geofence-alerts'),
  getGeofenceViolations: () => api.get('/locations/geofence-violations'),
  getLocationHistory: (vehicleId, params) => api.get(`/locations/vehicles/${vehicleId}/history`, { params }),
  getLiveTracking: () => api.get('/locations/live-tracking'),
  getRouteOptimization: (data) => api.post('/locations/route-optimization', data),
  getDistanceMatrix: (data) => api.post('/locations/distance-matrix', data),
};

// File upload helper
export const uploadFile = (file, type = 'general') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Bulk operations
export const bulkAPI = {
  importVehicles: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bulk/import/vehicles', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  importDrivers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bulk/import/drivers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  importCompanies: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bulk/import/companies', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  exportData: (type, filters) => api.post(`/bulk/export/${type}`, filters),
  getImportTemplates: () => api.get('/bulk/templates'),
  downloadImportTemplate: (type) => api.get(`/bulk/templates/${type}/download`, { responseType: 'blob' }),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
  checkDatabase: () => api.get('/health/database'),
  checkRedis: () => api.get('/health/redis'),
  checkStorage: () => api.get('/health/storage'),
};

// System endpoints
export const systemAPI = {
  getSystemInfo: () => api.get('/system/info'),
  getSystemMetrics: () => api.get('/system/metrics'),
  getSystemLogs: (params) => api.get('/system/logs', { params }),
  clearSystemCache: () => api.post('/system/clear-cache'),
  backupDatabase: () => api.post('/system/backup'),
  getBackupHistory: () => api.get('/system/backup-history'),
  restoreBackup: (backupId) => api.post(`/system/restore/${backupId}`),
  getSystemAlerts: () => api.get('/system/alerts'),
};

export default api;